/**
 * local_time_controller.js — the viewer's own clock under an admin timestamp.
 *
 * templates/macros/admin_time.html prints a time on the admin clock (Sydney)
 * with its zone, plus a hidden local line. This fills that line with the same
 * instant on the viewer's machine clock (its OS timezone setting), and only
 * when that clock reads differently: a viewer in Sydney or Melbourne sees no
 * second line. The date is added only when the viewer's day differs too.
 *
 * Markup: data-controller="local-time" data-local-time-zone-value="<IANA zone
 * the server printed>", a <time datetime> "shown" target, a "local" target.
 */
import { Controller } from "@hotwired/stimulus";

// en-AU names the Australian zones (AWST, ACST); elsewhere it falls back to
// offsets like GMT+1, which still read unambiguously.
const LOCALE = 'en-AU';
const DAY = { year: 'numeric', month: 'short', day: '2-digit' };
const TIME = { hour: 'numeric', minute: '2-digit' };

function parts(at, timeZone, fields) {
    const out = {};
    for (const p of new Intl.DateTimeFormat(LOCALE, { timeZone, ...fields }).formatToParts(at)) {
        out[p.type] = p.value;
    }
    return out;
}

function sameReading(at, zoneA, zoneB, fields) {
    const a = parts(at, zoneA, fields);
    const b = parts(at, zoneB, fields);
    return Object.keys(a).every((k) => a[k] === b[k]);
}

/**
 * "1:30 PM AWST (yours)", "Oct 04, 3:00 PM GMT+1 (yours)" when the day differs,
 * or null when the viewer's clock reads the same as shownZone's at that instant.
 */
export function localTimeLine(at, shownZone, viewerZone) {
    if (sameReading(at, shownZone, viewerZone, { ...DAY, ...TIME })) return null;
    const p = parts(at, viewerZone, { ...DAY, ...TIME, timeZoneName: 'short' });
    const time = `${p.hour}:${p.minute} ${p.dayPeriod.toUpperCase()} ${p.timeZoneName}`;
    const date = sameReading(at, shownZone, viewerZone, DAY) ? '' : `${p.month} ${p.day}, `;
    return `${date}${time} (yours)`;
}

export default class extends Controller {
    static targets = ['shown', 'local'];
    static values = { zone: String };

    connect() {
        const viewerZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const line = localTimeLine(new Date(this.shownTarget.dateTime), this.zoneValue, viewerZone);
        this.localTarget.textContent = line || '';
        this.localTarget.hidden = !line;
    }
}
